import { Component, Prop, Event, EventEmitter, Method, State, Element, h, Watch } from '@stencil/core';
import { Color, Size } from '../../interface';

@Component({
  tag: 'lar-range',
  styleUrl: 'range.scss',
  shadow: true
})
export class LarvaRangeNode {
  @Element() el!: HTMLElement;

  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'security';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Disable quck actions
   */
  @Prop() disableQuickActions: boolean = false;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() checked: boolean = false;
  @State() value: number = 0;
  @State() step: number = 1;
  @State() min: number = 0;
  @State() max: number = 100;
  @State() loading: boolean = true;

  private node!: HTMLLarNodeElement;

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.output.emit({}); // getFullState
  }
  /**
   * Larva error input
   */
  @Method()
  async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
    // revert last switch back if error response
    this.checked = !this.checked;
  }
  @Watch('value')
  changeToggle() {
    this.checked = Boolean(this.value);
  }

  /**
   * Larva input message
   */
  @Method()
  async input(data: any) {
    let value = parseFloat(data.value);
    if (isNaN(value)) {
      console.error(`lar-range receuived invalid message: ${JSON.stringify(data)}`);
      value = 0;
    }
    if (data.step) {
      const step = parseFloat(data.step);
      this.step = isNaN(step) ? this.step : step
    }
    if (data.min) {
      const min = parseFloat(data.min);
      this.min = isNaN(min) ? this.min : min
    }
    if (data.max) {
      const max = parseFloat(data.max);
      this.max = isNaN(max) ? this.max : max
    }
    this.value = value;
    this.loading = false;
  }

  private handleChange(value: number) {
    this.value = value;
  }

  private handleChangeToggle(value: boolean) {
    if (value === this.checked) {
      return;
    }
    if (value) {
      this.value = this.max;
    } else {
      this.value = this.min;
    }
    this.handleBlur();
  }

  private handleBlur() {
    if (!Number.isNaN(this.value) && this.loading === false) {
      this.loading = true;
      this.output.emit(this.value);
    }
  }

  render() {
    const componentProps = {
      onButtonclick: () => this.handleChangeToggle(!this.checked),
      onLarchange: (ev: CustomEvent) => this.handleChange(ev.detail.value),
      onLarblur: () => this.handleBlur(),
      buttonValue: this.checked ? 'onoff.switchoff' : 'onoff.switchon',
      disabled: this.loading,
      color: this.colorInputs || this.color,
      value: this.value,
      min: this.min,
      max: this.max,
      step: this.step
    };
    return [
      <lar-node
        value={String(this.value || 0)}
        hideTitles={this.hideTitles}
        icon={this.icon}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        colorIconSmall={this.colorIconSmall}
        loading={this.loading}
        log={this.log}
        nodeSize={this.nodeSize}
        component="range-content"
        componentProps={componentProps}
      >
        {this.disableQuickActions === false &&
          <lar-toggle
            slot="titles"
            onLarchange={ev => this.handleChangeToggle(ev.detail.checked)}
            checked={this.checked}
            color={this.colorInputs||this.color}
          />
        }
        <slot />
      </lar-node>
    ];
  }
}
